import React from 'react';
import { useSelector } from 'react-redux';
import localStorage from '@utils/localStorage';
import createKeyByUserName from '@utils/tempDashboard/createKeyByUserName';
import * as types from '@store/tempDashboard/types';
import commonSelector, {
  selectedCustomerIdSelector,
  selectedUserNameSelector,
} from '@store/common/selectors';
import { useLocation, useParams } from 'react-router-dom';
import AppSessionTimeline from '@src/components/app-session-timeline';
import { useGlobalFitlers } from './useGlobalFilters';
import { AppTimelinePageStyled } from './styled';
import featureNames from '@src/utils/featureToggle/constants';
import SessionTimelineWithFlow from '@src/components/app-session-timeline/SessionTimelineWithFlow';

const EMPTY_ARRAY = [];

const useGetLocalStorageKey = () => {
  const customerId = useSelector(selectedCustomerIdSelector);
  const userName = useSelector(selectedUserNameSelector);
  return createKeyByUserName(types.prefix, customerId, userName);
};

export const useDashboardTimelineRouteParams = () => {
  const params = useParams();
  const { id } = params;
  const location = useLocation();
  const { search } = location;

  const queryParams = new URLSearchParams(search);
  let clientId = queryParams.get('clientId');
  const viewerId = queryParams.get('userId');
  let interval = queryParams.get('interval');
  let source = queryParams.get('source');
  const key = queryParams.get('key');
  let globalFilters = useGlobalFitlers(key);
  let decodedViewerId = viewerId?.split(',');

  const localStorageKey = useGetLocalStorageKey();
  // get the values by local storage key
  const localStorageTempDashboards = localStorage.get(localStorageKey);
  const curDashbord = localStorageTempDashboards?.dashboards[id!];
  const {
    interval: dashboardInterval,
    source: dashboardSource,
    viewerId: dashboardViewerId,
    clientId: dashboardClientId,
    globalFilters: dashboardGF,
    eventNames,
    idType,
  } = curDashbord?.dashboard || {};

  interval = dashboardInterval ? dashboardInterval : interval;
  source = dashboardSource ? dashboardSource : source;
  globalFilters = dashboardGF ? dashboardGF : globalFilters;

  let impacted = false;
  if ((globalFilters && globalFilters.length > 0) || (eventNames && eventNames.length > 0)) {
    impacted = true;
  }

  // HACK:
  // sometimes there will be the situation when id is existed, but search is empty
  // currently cannot find the root cause since can't reproduce it every time
  if (id && !decodedViewerId && !clientId) {
    decodedViewerId = dashboardViewerId;
    clientId = dashboardClientId;
  }

  const flowId = queryParams.get('flowId') || curDashbord?.dashboard?.flowId;

  return {
    id,
    viewerId: decodedViewerId ?? '',
    clientId: clientId ?? '',
    interval: interval ?? '',
    source,
    globalFilters,
    impacted,
    eventNames: eventNames ?? EMPTY_ARRAY,
    idType,
    flowId,
  };
};

const AppTimelinePage = () => {
  const {
    id,
    viewerId,
    clientId,
    interval,
    source,
    globalFilters,
    impacted,
    eventNames,
    idType,
    flowId,
  } = useDashboardTimelineRouteParams();

  return (
    <AppTimelineDetail
      viewerId={viewerId}
      id={id}
      clientId={clientId}
      interval={interval}
      source={source}
      impacted={impacted}
      globalFilters={globalFilters}
      eventNames={eventNames}
      idType={idType}
      flowId={flowId}
    />
  );
};

const AppTimelineDetail = ({
  id,
  viewerId,
  clientId,
  interval,
  impacted,
  globalFilters,
  source,
  idType,
  eventNames,
  flowId,
}) => {
  const { features } = useSelector(commonSelector);
  const sessionTimelineWithFlow = features[featureNames.SESSION_TIMELINE_WITH_FLOW];

  // TODO: Deprecate
  return (
    <AppTimelinePageStyled>
      {sessionTimelineWithFlow ? (
        <SessionTimelineWithFlow
          id={id}
          viewerId={viewerId}
          clientId={clientId}
          interval={interval}
          impacted={impacted}
          source={source}
          globalFilters={globalFilters}
          idType={idType}
          eventNames={eventNames}
          flowId={flowId}
        />
      ) : (
        <AppSessionTimeline
          id={id}
          viewerId={viewerId}
          clientId={clientId}
          interval={interval}
          impacted={impacted}
          source={source}
          globalFilters={globalFilters}
          idType={idType}
          eventNames={eventNames}
          flowId={flowId}
        />
      )}
    </AppTimelinePageStyled>
  );
};

export default AppTimelinePage;
